/**
 * Copyright 2025 Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Amazon Software License (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *   http://aws.amazon.com/asl/
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts';
import { Spinner, Container, Box, Alert } from '@cloudscape-design/components';

/**
 * AuthCallback component handles the OAuth callback from Cognito
 * Exchanges the authorization code for tokens and redirects to the original destination
 */
export const AuthCallback: React.FC = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for error in URL params (Cognito error response)
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (errorParam) {
          setError(errorDescription || 'Authentication failed');
          return;
        }

        // Attempt to complete authentication
        const success = await checkAuth();

        if (success) {
          // Get the redirect path from session storage
          const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/';
          sessionStorage.removeItem('redirectAfterLogin');

          // Navigate to the original destination
          navigate(redirectPath, { replace: true });
        } else {
          setError('Failed to complete authentication. Please try again.');
        }
      } catch (err) {
        console.error('Error handling auth callback:', err);
        setError('An unexpected error occurred during authentication.');
      }
    };

    handleCallback();
  }, [checkAuth, navigate]);

  // If there's an error, show it with a retry option
  if (error) {
    return (
      <Container>
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <Alert type="error" header="Authentication Error">
            {error}
          </Alert>
          <Box padding={{ top: 'm' }}>
            <a href="/">Return to home</a>
          </Box>
        </Box>
      </Container>
    );
  }

  // Show loading spinner while processing callback
  return (
    <Container>
      <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
        <Spinner size="large" />
        <Box variant="p" padding={{ top: 'm' }}>
          Completing authentication...
        </Box>
      </Box>
    </Container>
  );
};
